import {Link, Outlet, useMatches, useParams} from "react-router-dom";
import {
    Avatar,
    Breadcrumb,
    Button,
    Dropdown,
    Flex,
    Layout,
    Menu,
    MenuProps, Modal,
    Space,
    theme, Tooltip
} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAngleDown,
    faAngleLeft,
    faAngleRight, faBolt,
    faBook, faCircleInfo, faFileExport, faGear, faHouse, faInfo
} from "@fortawesome/free-solid-svg-icons";
import SimpleDivider from "../../components/SimpleDivider";
import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../feature/tools";
import {fetchProjects, fetchTags, setProject} from "../../feature/project/projectsSlice";
import {ItemType, MenuItemType} from "antd/es/menu/interface";
import {Project as Proj} from "../../feature/project/project";
import Sider from "antd/es/layout/Sider";
import {Content, Header} from "antd/es/layout/layout";
import ProjectNotFound from "./ProjectNotFound";
import {pathMap} from "../../index";
import {Status} from "../../feature/project/status";
import {fetchCategories} from "../../feature/project/categoriesSlice";
import {exportStory, fetchStories} from "../../feature/project/storySlice";
import {Tools} from "../../ai/tools/components/Tools";
import {loadAiSettings, loadProjectAiProviders} from "../../feature/project/aiSlice";
import {UserMenu} from "../../components/UserMenu";
import {loadProjectStorageProvider} from "../../feature/project/storageSlice";
import {ProjectSettingsModal} from "./settings/ProjectSettings";
import {fetchContributors, fetchUser} from "../../feature/project/userSlice";
import {StoryTool, storyTools} from "../story/tools/StoryTool";
import {openProjectSettings} from "../../feature/project/appSettingsSlice";
import {User} from "../../feature/project/User";
import {Invitation} from "./Invitation";
import {editorRoles, WithUserPermission} from "../common/WithUserPermission";

export const Project = () => {
    const [collapsed, setCollapsed] = useState(true);
    const [showTools, setShowTools] = useState(false);
    let {projectId, storyId, shotId} = useParams();
    let matches = useMatches();
    let paths = matches.filter(item => item.id !== '1-3-1' && item.id !== '1-3-2' && item.id !== '1-1')
    const {
        token: {
            colorBgContainer,
            borderRadiusLG
        },
    } = theme.useToken();

    const dispatch = useAppDispatch()
    const projects = useAppSelector(state => state.projects.projects)
    const project = useAppSelector(state => state.projects.project)
    const user = useAppSelector(state => state.users.currentUser)
    const loadingStatus = useAppSelector(state => state.projects.status)
    const exportingStatus = useAppSelector(state => state.stories.status)
    const story = useAppSelector(state => state.stories.story)
    const chapter = useAppSelector(state => state.chapters.chapter)
    const act = useAppSelector(state => state.acts.act)
    const scene = useAppSelector(state => state.scenes.scene)
    const component = useAppSelector(state => state.components.component)
    const [selectedMenuItem, setSelectedMenuItem] = useState('-1')
    const [toolId, setToolId] = useState<StoryTool | undefined>(undefined)
    const shots = useAppSelector(state => state.shots.shots)

    const index = shots.findIndex(shot => shot.id == shotId)

    useEffect(() => {
            dispatch(fetchProjects())
            dispatch(fetchContributors(projectId ? parseInt(projectId) : undefined))
            dispatch(loadAiSettings())
            dispatch(fetchCategories())
            dispatch(fetchStories(projectId ? parseInt(projectId) : undefined))
        }, [projectId]
    )

    useEffect(() => {
        const pr = projects.find(item => item?.id.toString() === projectId)
        dispatch(setProject(pr))
        if (pr) {
            if (user && user.status == 'active') {
                dispatch(loadProjectStorageProvider({projectId: projectId}))
                dispatch(loadProjectAiProviders({projectId: projectId}))
            }
            dispatch(fetchTags({projectId: pr.id}))
        }
        setSelectedMenuItem('-1')
    }, [user, projectId, projects, dispatch])

    useEffect(() => {
        const toolId = storyTools.find(t => t.id == (matches[matches.length - 1].pathname.split("/").pop() ?? ''))
        setToolId(toolId)
        if (matches[matches.length - 1].pathname === paths[0].pathname
            || matches[matches.length - 1].pathname === paths[0].pathname + '/'
            || !toolId
        ) {
            setSelectedMenuItem('-1')
        } else if (toolId) {
            setSelectedMenuItem(toolId.id)
        }
    }, [matches, paths])

    useEffect(() => {
        dispatch(fetchUser())
    }, [])

    const items: MenuItemType[] = (projects || []).map(
        (project: Proj) => ({
            label:
                <Link to={`/projects/${project.id}`}>
                    <Space>
                        <Avatar shape={'square'} src={project?.icon} size={24}/>
                        {project.title}
                    </Space>
                </Link>,
            key: project.id,
        })
    );

    let menu: any[] = ([
        {type: 'divider'}
    ] as any[])
        .concat(user && user.role != 'viewer' && user.role != 'editor' ? [
            {
                key: 'settings',
                icon: <FontAwesomeIcon icon={faGear}/>,
                label: 'Settings',
                onClick: () => {
                    dispatch(openProjectSettings('general'))
                }
            }
        ] : [])
        .concat([
            {
                key: 'stories',
                icon: <FontAwesomeIcon icon={faBook}/>,
                label: <Link to={`/projects/${projectId}`}>Stories</Link>,
            },
            {type: 'divider'}
        ])
    if (projectId && storyId && user?.role != 'viewer') {
        menu = menu.concat(
            storyTools.map(tool => (
                    {
                        key: tool.link().split("/").pop() ?? tool.id,
                        icon: tool.icon,
                        disabled: !tool.enabled,
                        label: tool.enabled ? <Link to={tool.link(projectId, storyId)}>{tool.name}</Link> : tool.name,
                    }
                )
            ).flat()
        ).concat(
            {type: 'divider'},
        )
    }

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        dispatch(setProject(projects.find(item => item?.id.toString() === e.key)))
    };

    const menuProps = {
        items,
        onClick: handleMenuClick,
    };
    return (
        <Layout style={{
            height: "100%"
        }}>
            <Sider trigger={null} collapsedWidth={64} collapsible collapsed={collapsed} theme="light"
                   style={{boxShadow: '0 0 5px #e0e0e0'}}>
                <Flex justify={'space-between'} vertical={true} style={{height: '100%'}}>
                    <Flex vertical={true}>
                        <div style={{padding: '16px', fontSize: '24px'}}>
                            <Link to={'/'} style={{color: '#000000'}}>
                                <Flex align={'center'} gap={'small'} justify={collapsed ? 'center' : 'start'}
                                      style={{lineHeight: '24px'}}>
                                    <img alt={'logo'} src={'/logo.svg'} style={{width: 24}}/>
                                    {collapsed ? '' : 'narrator'}
                                </Flex>
                            </Link>
                        </div>
                        <div style={{padding: '4px'}}>
                            <Dropdown menu={menuProps} trigger={["click"]}>

                                <Button style={{
                                    width: '100%',
                                    padding: !collapsed ? '8px 20px' : '8px 16px',
                                    height: "auto",
                                }} type={'text'}>
                                    <Flex justify={'space-between'} align={'center'}>
                                        <Space>
                                            <Avatar src={project?.icon} shape={'square'} size={24}/>
                                            {collapsed ? "" :
                                                <div style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    maxWidth: 110
                                                }}>
                                                    {project?.title}
                                                </div>
                                            }
                                        </Space>
                                        {!collapsed ? <FontAwesomeIcon icon={faAngleDown}/> : ''}
                                    </Flex>
                                </Button>

                            </Dropdown>
                        </div>
                        {!user || user.status != 'active' || !projects.find(item => item?.id.toString() === projectId) ? '' : <>
                            <Menu
                                style={{borderInlineEnd: 'none'}}
                                theme="light"
                                mode="inline"
                                defaultSelectedKeys={[selectedMenuItem]}
                                items={menu}
                                selectedKeys={[selectedMenuItem]}
                                onClick={(e) => setSelectedMenuItem(e.key)}
                            />

                            {storyId && user && user.role != 'viewer' && user.role != 'editor' ?
                                <>
                                    <div style={{padding: 16}}>
                                        <Button
                                            type="primary"
                                            style={{width: '100%'}}
                                            loading={exportingStatus == Status.Exporting}
                                            icon={<FontAwesomeIcon icon={faFileExport}/>}
                                            onClick={() => {
                                                if (projectId && storyId) {
                                                    dispatch(exportStory({
                                                        projectId: parseInt(projectId),
                                                        storyId: parseInt(storyId)
                                                    }))
                                                }
                                            }}
                                        >
                                            {collapsed ? "" : "Export"}
                                        </Button>
                                    </div>
                                    <SimpleDivider/>
                                </>
                                : ''}</>
                        }
                    </Flex>
                    <Tooltip title={'Build number: ' + process.env.REACT_APP_BUILD_NUMBER} placement={'right'}>
                        <FontAwesomeIcon style={{padding: 20}} icon={faCircleInfo} size={'lg'}/>
                    </Tooltip>
                </Flex>
                <div style={{
                    position: 'absolute',
                    top: 20,
                    right: '-12px',
                    width: 24,
                    boxShadow: '0 0 5px #e0e0e0',
                    height: 24,
                    padding: 0,
                    borderRadius: 12,
                    background: colorBgContainer,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                     onClick={() => setCollapsed(!collapsed)}>
                    <FontAwesomeIcon icon={collapsed ? faAngleRight : faAngleLeft}/>
                </div>
            </Sider>
            <Layout>
                <Header style={{padding: '0 20px 0 40px', background: colorBgContainer}}>
                    <Flex justify={'space-between'} align={'center'} style={{height: '100%'}}>
                        <Breadcrumb
                            items={
                                paths.filter(item => item.id !== '1-3-1' && item.id !== '1-3-2' && item.id !== '1-1').map(match => (
                                    match?.id === '1' ?
                                        {
                                            title: paths[paths.length - 1] === match ?
                                                <Space size={4}><FontAwesomeIcon
                                                    icon={faHouse}/> {project?.title}</Space> :
                                                <Link to={`/projects/${projectId}`}> <Space
                                                    size={4}><FontAwesomeIcon
                                                    icon={faHouse}/> {project?.title}</Space></Link>,
                                        } :
                                        match?.id === '1-3-2-1' ?
                                            {
                                                title: paths[paths.length - 1] === match ? story?.title : <Link
                                                    to={match?.pathname.slice(0, match?.pathname.lastIndexOf('/'))}>{story?.title}</Link>,
                                            } :
                                            match?.id === '1-3-2-4' ?
                                                {
                                                    title: paths[paths.length - 1] === match ? act?.title :
                                                        <Link to={match?.pathname}>{act?.title}</Link>,
                                                }
                                                :
                                                match?.id === '1-3-2-5' ?
                                                    {
                                                        title: paths[paths.length - 1] === match ? chapter?.title :
                                                            <Link to={match?.pathname}>{chapter?.title}</Link>,
                                                    }
                                                    :
                                                    match?.id === '1-3-2-6' ?
                                                        {
                                                            title: paths[paths.length - 1] === match ? scene?.title :
                                                                <Link
                                                                    to={match?.pathname}>{scene?.title}</Link>,
                                                        }
                                                        :
                                                        match?.id === '1-3-2-7' ?
                                                            {
                                                                title: paths[paths.length - 1] === match ? 'Shot ' + (index > -1 ? (index + 1) : "") :
                                                                    <Link to={match?.pathname}>Shot {index > -1 ? (index + 1) : ""}</Link>,
                                                            }
                                                            :
                                                            match?.id === '1-4-1' || match?.id === '1-3-2-2-1' || match?.id === '1-3-2-3-1' ?
                                                                {
                                                                    title: component?.title,
                                                                    href: paths[paths.length - 1] === match ? undefined : match?.pathname
                                                                }
                                                                :
                                                                toolId != undefined ?
                                                                    {
                                                                        title: toolId.name,
                                                                    }
                                                                    :
                                                                    {
                                                                        title: paths[paths.length - 1] === match ? pathMap.find(item => item.id === match?.id)?.title :
                                                                            <Link
                                                                                to={match?.pathname}>{pathMap.find(item => item.id === match?.id)?.title}</Link>,
                                                                    }
                                ))
                            }
                        />

                        <Flex align={'center'} gap={16}>
                            <Modal
                                title={"Tools"}
                                open={showTools}
                                onCancel={() => setShowTools(false)}
                                okButtonProps={{style: {display: 'none'}}}
                                cancelText={'Close'}
                                width={1000}
                            >

                                <Tools onClose={() => setShowTools(false)}/>
                            </Modal>
                            <WithUserPermission roles={editorRoles}>
                                <Button style={{zIndex: 10000}} icon={<FontAwesomeIcon icon={faBolt}/>}
                                        onClick={() => setShowTools(true)}>Tools</Button>
                            </WithUserPermission>
                            <UserMenu/>
                        </Flex>
                    </Flex>
                </Header>
                <Content
                    style={{
                        margin: '16px',
                        padding: 24,
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                    }}
                >
                    {
                        project && !projects.find(item => item?.id.toString() === projectId) ?
                            <ProjectNotFound/>
                            :
                            user && user.status == 'invited' ?
                                <Invitation/>
                                :
                                <Outlet/>}
                </Content>
                <ProjectSettingsModal/>
            </Layout>
        </Layout>
    );
};

export default Project;